import { AxiosResponse } from "axios";
import { getRequest } from "./httpRequest";

// "Another fact" goes through the facts in order, from the first, and starts
// again after the last; the server wraps the position round. The place is kept
// in the browser, so someone who comes back carries on where they left off
// rather than seeing the first few again. Shared by the profile's "Did you
// know?" pop-up and the team page's fact of the day.
const POSITION_KEY = "funFactPosition";

// Used while the browser won't let us save, so the order still holds for the
// rest of the visit. Some browsers let us read but not save, so this is checked
// before the saved place, which would otherwise never move on.
let unsavedPosition: number | null = null;

const readPosition = (): number => {
  if (unsavedPosition !== null) return unsavedPosition;

  try {
    const position = Number(localStorage.getItem(POSITION_KEY) || "0");
    return Number.isInteger(position) && position >= 0 ? position : 0;
  } catch {
    return 0;
  }
};

const savePosition = (position: number) => {
  try {
    localStorage.setItem(POSITION_KEY, String(position));
    unsavedPosition = null;
  } catch {
    unsavedPosition = position;
  }
};

// For someone who has never been shown a fact, starts the order at a random
// place rather than the first, so new visitors don't all read the same few.
// The server wraps the position round, so any number will do; a big range
// keeps every fact about equally likely. Used by the brochure box only.
export const startFunFactsAnywhere = () => {
  if (unsavedPosition !== null) return;

  try {
    if (localStorage.getItem(POSITION_KEY) !== null) return;
  } catch {
    // Can't read it, so it can't have been saved either.
  }
  savePosition(Math.floor(Math.random() * 1000000));
};

// The fact already on screen is passed in so the next one is never the same
// fact again (the pop-up opens on the day's fact, which is somewhere in the
// order too).
export const fetchNextFunFact = (current?: string, skips = 1): Promise<string> => {
  const position = readPosition();

  // Move on only once the fact has arrived, so a failed request doesn't skip
  // one nobody saw. Both buttons are disabled while a fact loads.
  return getRequest(`/api/fun_facts.json?position=${position}`).then(
    (response: AxiosResponse<string>) => {
      savePosition(position + 1);

      return response.data === current && skips > 0
        ? fetchNextFunFact(current, skips - 1)
        : response.data;
    },
  );
};
